/**
 * Helper para duplicar pedidos
 * Mapea los datos de un pedido existente a la estructura del formulario de nuevo pedido
 */

/**
 * Mapea un pedido completo a la estructura del formulario
 * @param {Object} pedido - Pedido original de SAP (completo desde API)
 * @param {Function} onProgress - Callback opcional para reportar progreso (step, progress)
 * @returns {Object} Datos mapeados para el formulario
 */
export function mapPedidoToOrderForm(pedido, onProgress) {
  const totalSteps = 6;
  let currentStep = 0;

  const reportProgress = (step, message) => {
    currentStep = step;
    const progress = (step / totalSteps) * 100;
    if (onProgress) {
      onProgress({ step, progress, message });
    }
  };

  reportProgress(1, "Mapeando graduaciones...");
  // `listOJOIN` puede venir como array directo o wrapped en `{ clsZES_OJO_INPUT: [...] }`
  // según el endpoint SAP. Normalizamos antes de buscar OD/OI; sin esto
  // `find` se llama sobre el wrapper y devuelve `undefined` → se pierden
  // todos los campos por ojo (IDL_BIGEXACT incluido).
  const rawOjoIn = pedido.oClaseBAPI?.listOJOIN;
  const listOJOIN = Array.isArray(rawOjoIn)
    ? rawOjoIn
    : Array.isArray(rawOjoIn?.clsZES_OJO_INPUT)
      ? rawOjoIn.clsZES_OJO_INPUT
      : [];
  const ojoOD = listOJOIN.find((o) => o?.ZOJO === "D") || null;
  const ojoOI = listOJOIN.find((o) => o?.ZOJO === "I") || null;


  const vkorg = pedido.oClaseBAPI?.CABOUT?.VKORG || pedido.oClaseBAPI?.VKORG || null;

  // Extract reference
  let reference = "";
  // Check listCABIN first as indicated by user
  if (pedido.oClaseBAPI?.listCABIN?.[0]?.REF_CLIENT) {
    reference = pedido.oClaseBAPI.listCABIN[0].REF_CLIENT;
  } else if (pedido.oClaseBAPI?.listCABIN?.clsZES_CAB_INPUT?.[0]?.REF_CLIENT) {
    reference = pedido.oClaseBAPI.listCABIN.clsZES_CAB_INPUT[0].REF_CLIENT;
  } else if (pedido.oClaseBAPI?.listCABOUT?.clsZES_CAB_OUTPUT?.[0]?.REF_CLIENTE) {
    reference = pedido.oClaseBAPI.listCABOUT.clsZES_CAB_OUTPUT[0].REF_CLIENTE;
  } else if (pedido.oClaseBAPI?.CABOUT?.REF_CLIENTE) {
    reference = pedido.oClaseBAPI.CABOUT.REF_CLIENTE;
  }

  const cabin =
    pedido.oClaseBAPI?.listCABIN?.clsZES_CAB_INPUT?.[0] || pedido.oClaseBAPI?.listCABIN?.[0];
  const montaje = cabin?.MONTAJE === "X" ? "X" : null;
  const codigoPromocional = cabin?.CODI_PROM || "";

  const mappedData = {
    od: mapGraduation(ojoOD),
    oi: mapGraduation(ojoOI),
    brand: mapBrand(vkorg),
    vkorg,
    reference,
    montaje,
    codigoPromocional,
    fromDuplicatedOrder: true,
  };

  // Mapear datos de lente desde el primer ojo (ambos ojos tienen los mismos datos de lente)
  reportProgress(2, "Mapeando datos de lente...");
  const lensData = ojoOD || ojoOI;
  if (lensData) {
    mappedData.lens = {
      lente: lensData.JER_EDI_INET || null,
      diametro: lensData.DIAMETRO || null,
      fotocromatico: lensData.FOTOCROMICO || lensData.FOTOCROMATICO || null,
      focomaterial: lensData.FOCO_MATERIAL || null,
      indice: lensData.IND_REF || null,
      coloracion: lensData.COLORACION || null,
      color: lensData.COLOR || null,
      tono: lensData.TONO || null,
      tratamiento: lensData.TRATAM1 || null,
    };
  }

  // Mapear datos adicionales desde ambos ojos
  reportProgress(3, "Mapeando datos adicionales...");
  if (ojoOD || ojoOI) {
    mappedData.additionalData = {
      // Campos por ojo
      altAroOD: ojoOD?.ALT_ARO || null,
      altAroOI: ojoOI?.ALT_ARO || null,
      distNpOD: ojoOD?.DIST_NP || null,
      distNpOI: ojoOI?.DIST_NP || null,
      distVertOD: ojoOD?.DIST_VERT || null,
      distVertOI: ojoOI?.DIST_VERT || null,
      ffwMaxOD: ojoOD?.FFW_MAX || null,
      ffwMaxOI: ojoOI?.FFW_MAX || null,
      ffwMinOD: ojoOD?.FFW_MIN || null,
      ffwMinOI: ojoOI?.FFW_MIN || null,
      // Campos generales (tomar del primer ojo disponible)
      gradPantos: (ojoOD || ojoOI)?.GRAD_PANTOS || null,
      gradEnvolv: (ojoOD || ojoOI)?.GRAD_ENVOLV || null,
      distTrab: (ojoOD || ojoOI)?.DIST_TRAB || null,
      garantiaNom: (ojoOD || ojoOI)?.GARANTIA_NOM || null,
      garantia1Ap: (ojoOD || ojoOI)?.GARANTIA_1AP || null,
      garantia2Ap: (ojoOD || ojoOI)?.GARANTIA_2AP || null,
      vmap: (ojoOD || ojoOI)?.VMAP || null,
      iniciales: (ojoOD || ojoOI)?.INICIALES || null,
      idlBigExact:
        (ojoOD || ojoOI)?.IDL_BIGEXACT ||
        (ojoOD || ojoOI)?.idlBigExact ||
        (ojoOD || ojoOI)?.idl_bigexact ||
        null,
      cmimetikaOD: ojoOD?.CMIMETIKA || null,
      cmimetikaOI: ojoOI?.CMIMETIKA || null,
      // Nuevos campos mapeados
      claseProg: (ojoOD || ojoOI)?.CLASE_PROG || null,
      tipoProg: (ojoOD || ojoOI)?.TIPO_PROG || null,
      altPas: (ojoOD || ojoOI)?.ALT_PAS || null,
      visualPref: (ojoOD || ojoOI)?.VISUAL_PREF || null,
      inset0OD: ojoOD?.INSET0 || null,
      inset0OI: ojoOI?.INSET0 || null,
      isotipo: ojoOD?.ISOTIPO || ojoOI?.ISOTIPO || null,
      isotipoRod: ojoOD?.ISOTIPO_ROD || ojoOI?.ISOTIPO_ROD || null,
      tipoIsotipo: ojoOD?.TIPO_ISOTIPO || ojoOI?.TIPO_ISOTIPO || null,
    };

    // Guardar también los datos de los ojos para usar como fallback
    mappedData.eyeData = {
      ojoOD,
      ojoOI,
    };
  }

  // Mapear datos de formas y espesores desde listCABFRM
  reportProgress(4, "Mapeando formas y espesores...");
  let cabfrm = null;

  if (pedido.oClaseBAPI?.listCABFRM?.clsZES_CAB_FORMA?.[0]) {
    cabfrm = pedido.oClaseBAPI.listCABFRM.clsZES_CAB_FORMA[0];
  } else if (Array.isArray(pedido.oClaseBAPI?.listCABFRM) && pedido.oClaseBAPI.listCABFRM[0]) {
    cabfrm = pedido.oClaseBAPI.listCABFRM[0];
  } else if (
    pedido.oClaseBAPI?.listCABFRM?.clsZES_CAB_FORMA &&
    !Array.isArray(pedido.oClaseBAPI.listCABFRM.clsZES_CAB_FORMA)
  ) {
    cabfrm = pedido.oClaseBAPI.listCABFRM.clsZES_CAB_FORMA;
  }

  if (cabfrm) {
    mappedData.shapeData = {
      origenDatos: cabfrm.ORIGEN_DATOS || null,
      formaBasica: cabfrm.FORMA_BASICA || null,
      calibre: cabfrm.CALIBRE || null,
      puente: cabfrm.PUENTE || null,
      altBox: cabfrm.ALT_BOX || null,
      montura: cabfrm.MONTURA || null,
      marca: cabfrm.MARCA || null,
      modelo: cabfrm.MODELO || null,
      tipoMontura: cabfrm.TIPO_MONTURA || null,
      bisel: cabfrm.BISEL || null,
      orientacion: cabfrm.ORIENTACION || null,
      redimension: {
        TEM_SIGN: cabfrm.TEM_SIGN,
        TEM_COTA: cabfrm.TEM_COTA,
        SUP_SIGN: cabfrm.SUP_SIGN,
        SUP_COTA: cabfrm.SUP_COTA,
        INF_SIGN: cabfrm.INF_SIGN,
        INF_COTA: cabfrm.INF_COTA,
        TOT_SIGN: cabfrm.TOT_SIGN,
        TOT_COTA: cabfrm.TOT_COTA,
      },
    };
  }

  // Mapear datos de suplementos
  reportProgress(5, "Mapeando suplementos...");
  const suplementosData = mapSuplements(ojoOD, ojoOI);
  if (suplementosData) {
    mappedData.suplementosData = suplementosData;
  }

  // Mapear listFORMAS si existe
  if (pedido.oClaseBAPI?.listFORMAS?.clsZFORMAS) {
    mappedData.listFORMAS = pedido.oClaseBAPI.listFORMAS;
  }

  // Guardar referencia al pedido original completo para guardarlo en el store
  mappedData.pedidoOriginal = pedido.oClaseBAPI;

  reportProgress(6, "Finalizando...");
  return mappedData;
}

/**
 * Mapea los datos de graduación de un ojo
 * @param {Object} ojo - Datos del ojo desde listOJOIN
 * @returns {Object|null} Datos de graduación mapeados
 */
function mapGraduation(ojo) {
  if (!ojo) {
    return null;
  }


  // El cilindro nunca debe llegar al formulario como 0: SAP devuelve "0.00"
  // (o vacío, según el campo) en pedidos sin astigmatismo, y aplicar ese 0
  // literal deja el pedido irreenviable — `validacionPedido` lo rechaza con
  // "el cilindro debe estar vacío o tener un valor distinto de 0". Mismo
  // criterio que ya aplica RecuperarReceta (stores/indo.js).
  const cilindro = parseSignedValue(ojo.SIGNO_CIL, ojo.CILINDRO);
  const sinCilindro = cilindro === null || cilindro === 0;
  const eje = parseFloat(ojo.EJE);

  const mapped = {
    esfera: parseSignedValue(ojo.SIGNO_ESF, ojo.ESFERA),
    cilindro: sinCilindro ? "" : cilindro,
    // Sin cilindro tampoco puede viajar el eje: `validacionPedido` rechaza el
    // pedido con "eje_cilindro_obligatorios" si EJE viene informado y
    // CILINDRO está vacío.
    eje: sinCilindro || !Number.isFinite(eje) ? null : eje,
    adicion: parseFloat(ojo.ADICION) || null,
    stock: ojo.SOLO_STOCK === "X",
    informativo: ojo.SOLO_INFOR === "X",
    isFavorito: false,
  };

  return mapped;
}

/**
 * Parsea un valor numérico con su signo.
 *
 * Devuelve `null` (no 0) cuando el BAPI no trae valor: un 0 inventado se
 * renderiza como "+0.00" en el formulario y se reenvía a SAP como un dato real.
 * @param {string} signo - Signo del valor ('+' o '-')
 * @param {string|number} valor - Valor numérico
 * @returns {number|null} Valor con signo aplicado, o null si viene vacío/no numérico
 */
export function parseSignedValue(signo, valor) {
  // Manejar casos donde valor puede ser null, undefined, o string vacío
  if (valor === null || valor === undefined || valor === "") {
    return null;
  }

  const num = parseFloat(valor);

  if (isNaN(num)) {
    return null;
  }

  // Aplicar signo
  return signo === "-" ? -num : num;
}

// Funciones para extensibilidad futura:

/**
 * Mapea la marca del pedido
 * @param {string} vkorg - Código de organización de ventas
 * @returns {string} Marca mapeada
 */
export function mapBrand(vkorg) {
  const brandMap = {
    "0803": "indo",
    "0808": "rodenstock",
    "0809": "optovision",
  };
  return brandMap[vkorg] || "indo";
}

/**
 * Mapea información de la lente
 * @param {Object} ojoOD - Datos del ojo derecho
 * @param {Object} ojoOI - Datos del ojo izquierdo
 * @returns {Object} Información de lente mapeada
 */
export function mapLensInfo(ojoOD, ojoOI) {
  // TODO: Implementar cuando se necesite
  return {
    fotocromatico:
      ojoOD?.FOTOCROMICO ||
      ojoOD?.FOTOCROMATICO ||
      ojoOI?.FOTOCROMICO ||
      ojoOI?.FOTOCROMATICO ||
      null,
    focoMaterial: ojoOD?.FOCO_MATERIAL || null,
    indice: ojoOD?.IND_REF || null,
    tratamiento: ojoOD?.TRATAM1 || null,
    // ... más campos
  };
}

/**
 * Mapea información adicional del pedido
 * @param {Object} pedido - Pedido original
 * @returns {Object} Información adicional mapeada
 */
export function mapAditionalInfo(pedido) {
  // TODO: Implementar cuando se necesite
  return {};
}

/**
 * Mapea suplementos del pedido
 * @param {Object} ojoOD - Datos del ojo derecho
 * @param {Object} ojoOI - Datos del ojo izquierdo
 * @returns {Object} Suplementos mapeados
 */
export function mapSuplements(ojoOD, ojoOI) {
  const suplementosData = {
    od: {
      // Descentramiento es siempre positivo, solo usar COTA_DESN
      descentramiento: ojoOD?.COTA_DESN || null,
      baseEspecial: ojoOD?.BASE || ojoOD?.BASE_NOMINAL || null,
      prisma1: {
        dioptrias: ojoOD?.DIOP_PR1 || null,
        base: ojoOD?.BASE_PR1 || null,
        grados: ojoOD?.GRAD_PR1 || null,
      },
      prisma2: {
        dioptrias: ojoOD?.DIOP_PR2 || null,
        base: ojoOD?.BASE_PR2 || null,
        grados: ojoOD?.GRAD_PR2 || null,
      },
    },
    oi: {
      // Descentramiento es siempre positivo, solo usar COTA_DESN
      descentramiento: ojoOI?.COTA_DESN || null,
      baseEspecial: ojoOI?.BASE || ojoOI?.BASE_NOMINAL || null,
      prisma1: {
        dioptrias: ojoOI?.DIOP_PR1 || null,
        base: ojoOI?.BASE_PR1 || null,
        grados: ojoOI?.GRAD_PR1 || null,
      },
      prisma2: {
        dioptrias: ojoOI?.DIOP_PR2 || null,
        base: ojoOI?.BASE_PR2 || null,
        grados: ojoOI?.GRAD_PR2 || null,
      },
    },
  };

  // Solo retornar si hay algún campo no vacío
  const hasSuplementos =
    (suplementosData.od.descentramiento != null && suplementosData.od.descentramiento !== "") ||
    (suplementosData.od.baseEspecial != null && suplementosData.od.baseEspecial !== "") ||
    (suplementosData.od.prisma1.dioptrias != null && suplementosData.od.prisma1.dioptrias !== "") ||
    (suplementosData.od.prisma1.base != null && suplementosData.od.prisma1.base !== "") ||
    (suplementosData.od.prisma1.grados != null && suplementosData.od.prisma1.grados !== "") ||
    (suplementosData.od.prisma2.dioptrias != null && suplementosData.od.prisma2.dioptrias !== "") ||
    (suplementosData.od.prisma2.base != null && suplementosData.od.prisma2.base !== "") ||
    (suplementosData.od.prisma2.grados != null && suplementosData.od.prisma2.grados !== "") ||
    (suplementosData.oi.descentramiento != null && suplementosData.oi.descentramiento !== "") ||
    (suplementosData.oi.baseEspecial != null && suplementosData.oi.baseEspecial !== "") ||
    (suplementosData.oi.prisma1.dioptrias != null && suplementosData.oi.prisma1.dioptrias !== "") ||
    (suplementosData.oi.prisma1.base != null && suplementosData.oi.prisma1.base !== "") ||
    (suplementosData.oi.prisma1.grados != null && suplementosData.oi.prisma1.grados !== "") ||
    (suplementosData.oi.prisma2.dioptrias != null && suplementosData.oi.prisma2.dioptrias !== "") ||
    (suplementosData.oi.prisma2.base != null && suplementosData.oi.prisma2.base !== "") ||
    (suplementosData.oi.prisma2.grados != null && suplementosData.oi.prisma2.grados !== "");

  return hasSuplementos ? suplementosData : null;
}
